import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

interface UserLookupResult {
  success: boolean;
  auth0Id: string;
  displayName: string;
  source: 'postgresql' | 'mongodb' | 'not_found';
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

interface UserCache {
  [auth0Id: string]: {
    displayName: string;
    source: string;
    timestamp: number;
  };
}

// Global cache shared across all hook instances
const globalUserCache: UserCache = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Pending requests to avoid duplicate API calls
const pendingRequests: Map<string, Promise<UserLookupResult>> = new Map();

/**
 * Check if a string looks like an Auth0 ID
 */
export function isAuth0Id(value: string | undefined | null): boolean {
  if (!value) return false;
  // Strip @auth0.local suffix if present (fallback email format)
  const cleanValue = value.endsWith('@auth0.local') ? value.replace('@auth0.local', '') : value;
  return cleanValue.includes('|') && (
    cleanValue.startsWith('auth0|') ||
    cleanValue.startsWith('google-oauth2|') ||
    cleanValue.startsWith('facebook|') ||
    cleanValue.startsWith('twitter|') ||
    cleanValue.startsWith('linkedin|') ||
    cleanValue.startsWith('github|') ||
    cleanValue.startsWith('windowslive|') ||
    cleanValue.startsWith('apple|')
  );
}

/**
 * Clean Auth0 ID by removing @auth0.local suffix
 */
export function cleanAuth0Id(value: string): string {
  if (value.endsWith('@auth0.local')) {
    return value.replace('@auth0.local', '');
  }
  return value;
}

/**
 * Lookup a single user by Auth0 ID
 */
async function lookupUser(auth0Id: string): Promise<UserLookupResult> {
  // Clean the Auth0 ID (remove @auth0.local suffix)
  const cleanedId = cleanAuth0Id(auth0Id);

  // Check cache first (use cleaned ID)
  const cached = globalUserCache[cleanedId];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return {
      success: true,
      auth0Id: cleanedId,
      displayName: cached.displayName,
      source: cached.source as 'postgresql' | 'mongodb' | 'not_found',
    };
  }

  // Check if there's already a pending request for this ID
  const existingRequest = pendingRequests.get(cleanedId);
  if (existingRequest) {
    return existingRequest;
  }

  // Make API call with cleaned ID
  const promise = api.get<UserLookupResult>('/api/auth/user-lookup', {
    params: { auth0Id: cleanedId }
  }).then(response => {
    const result = response.data;

    // Cache the result (use cleaned ID as key)
    globalUserCache[cleanedId] = {
      displayName: result.displayName,
      source: result.source,
      timestamp: Date.now(),
    };

    // Clean up pending request
    pendingRequests.delete(cleanedId);

    return result;
  }).catch(error => {
    console.error('User lookup failed:', error);
    pendingRequests.delete(cleanedId);

    // Return a fallback
    const fallback = formatAuth0IdFallback(cleanedId);
    return {
      success: false,
      auth0Id: cleanedId,
      displayName: fallback,
      source: 'not_found' as const,
    };
  });

  pendingRequests.set(cleanedId, promise);
  return promise;
}

/**
 * Batch lookup multiple users
 */
async function batchLookupUsers(auth0Ids: string[]): Promise<Map<string, string>> {
  const results = new Map<string, string>();
  const uncachedIds: string[] = [];

  // Check cache first
  for (const id of auth0Ids) {
    const cached = globalUserCache[id];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      results.set(id, cached.displayName);
    } else {
      uncachedIds.push(id);
    }
  }

  // Fetch uncached IDs
  if (uncachedIds.length > 0) {
    try {
      const response = await api.post<{
        success: boolean;
        results: Record<string, { displayName: string; source: string }>;
      }>('/api/auth/user-lookup/batch', { auth0Ids: uncachedIds });

      const apiResults = response.data.results || {};
      for (const [auth0Id, info] of Object.entries(apiResults)) {
        results.set(auth0Id, info.displayName);
        // Also map the raw ID with @auth0.local suffix
        const rawId = uncachedIds.find(id => cleanAuth0Id(id) === auth0Id);
        if (rawId && rawId !== auth0Id) results.set(rawId, info.displayName);

        globalUserCache[auth0Id] = {
          displayName: info.displayName,
          source: info.source,
          timestamp: Date.now(),
        };
      }
    } catch (error) {
      console.error('Batch user lookup failed:', error);
      // Use fallback for failed lookups
      for (const id of uncachedIds) {
        if (!results.has(id)) {
          results.set(id, formatAuth0IdFallback(id));
        }
      }
    }
  }

  return results;
}

/**
 * Format Auth0 ID as fallback (last resort)
 */
function formatAuth0IdFallback(auth0Id: string): string {
  if (!auth0Id) return '';
  if (auth0Id.includes('|')) {
    const parts = auth0Id.split('|');
    if (parts.length === 2) {
      return `User ${parts[1].substring(0, 6)}`;
    }
  }
  return auth0Id;
}

/**
 * Hook to lookup a single user's display name
 *
 * GP-1450: the id this hook is asked about can CHANGE on a mounted instance —
 * a recycled MUI DataGrid cell scrolls a new row into the same position, a
 * dialog is closed and reopened on a different record. The effect below
 * therefore has to discard a response that answers an id we are no longer
 * asking about; without that, the last request to LAND wins regardless of which
 * id it answered, and a slow lookup for user A silently overwrites a completed
 * one for user B — leaving B labelled with A's name for as long as the
 * component stays mounted. `resolvedFor` lets callers prove the name they are
 * about to render belongs to the id they asked about.
 */
export function useUserLookup(auth0Id: string | undefined | null): {
  displayName: string;
  loading: boolean;
  source: string | null;
  /**
   * The id `displayName` currently describes. Always in step with it, so a
   * caller can gate on `resolvedFor === theIdIAskedAbout` and never render a
   * name that belongs to a different user. Null only when nothing was asked.
   */
  resolvedFor: string | null;
} {
  const [displayName, setDisplayName] = useState<string>(() => {
    // Initialize from cache if available
    if (auth0Id && globalUserCache[auth0Id]) {
      return globalUserCache[auth0Id].displayName;
    }
    return auth0Id || '';
  });
  const [resolvedFor, setResolvedFor] = useState<string | null>(auth0Id || null);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  // See the note on `requestIdRef` in useBatchUserLookup — same race, and here a
  // stale completion shows the PREVIOUS row's/order's user under the new one's
  // name (GP-1485).
  const requestIdRef = useRef(0);

  useEffect(() => {
    // Bump first, so an in-flight lookup started for the previous id can no
    // longer write to state — including on the early-return paths below.
    const requestId = ++requestIdRef.current;

    if (!auth0Id || !isAuth0Id(auth0Id)) {
      setDisplayName(auth0Id || '');
      setResolvedFor(auth0Id || null);
      setLoading(false);
      setSource(null);
      return;
    }

    // Check cache
    const cached = globalUserCache[auth0Id];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setDisplayName(cached.displayName);
      setResolvedFor(auth0Id);
      setSource(cached.source);
      setLoading(false);
      return;
    }

    // Discard a response for an id we have since moved off (see the note
    // above). Merge-train 2026-08-27: #2343's requestIdRef guard subsumes
    // #2331's per-effect `cancelled` flag — one mechanism, and the same ref
    // also covers unmount via the cleanup bump below.
    setLoading(true);
    lookupUser(auth0Id).then(result => {
      if (requestId !== requestIdRef.current) return; // a newer id is being resolved
      setDisplayName(result.displayName);
      setResolvedFor(auth0Id);
      setSource(result.source);
      setLoading(false);
    });

    return () => {
      requestIdRef.current++;
    };
  }, [auth0Id]);

  return { displayName, loading, source, resolvedFor };
}

/**
 * Hook to lookup multiple users' display names
 */
export function useBatchUserLookup(auth0Ids: (string | undefined | null)[]): {
  userNames: Map<string, string>;
  loading: boolean;
} {
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const prevIdsRef = useRef<string>('');
  // GP-1485: which id set is currently being resolved. Navigating between two
  // order-detail URLs re-keys this hook while the first batch is still in
  // flight; without this guard the older response can land last and overwrite
  // the new order's names with the previous order's map. `prevIdsRef` has
  // already advanced by then, so nothing refetches and the current ids stay
  // unresolved — which is exactly how a real counsellor renders as "Unknown
  // user". A completion whose id is no longer current is dropped.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const validIds = auth0Ids.filter((id): id is string => !!id && isAuth0Id(id));
    const idsKey = validIds.sort().join(',');

    // Skip if IDs haven't changed
    if (idsKey === prevIdsRef.current) {
      return;
    }
    prevIdsRef.current = idsKey;

    // Bump before the empty-set early return too: clearing the map must also
    // invalidate a lookup started for the ids we just navigated away from.
    const requestId = ++requestIdRef.current;

    if (validIds.length === 0) {
      setUserNames(new Map());
      setLoading(false);
      return;
    }

    setLoading(true);
    batchLookupUsers(validIds).then(results => {
      if (requestId !== requestIdRef.current) return; // a newer id set is in flight
      setUserNames(results);
      setLoading(false);
    });
  }, [auth0Ids]);

  return { userNames, loading };
}

/**
 * Get user display name with MongoDB lookup (for use in components)
 * Returns the cached value immediately if available, or the raw ID while loading
 */
export function getUserDisplayName(auth0Id: string | undefined | null): string {
  if (!auth0Id) return '';

  // Not an Auth0 ID, return as-is
  if (!isAuth0Id(auth0Id)) {
    return auth0Id;
  }

  // Check cache
  const cached = globalUserCache[auth0Id];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.displayName;
  }

  // Trigger async lookup (result will be available next render)
  lookupUser(auth0Id);

  // Return fallback while loading
  return formatAuth0IdFallback(auth0Id);
}

/**
 * Get user display name with MongoDB lookup (ASYNC version)
 * Use this in event handlers/snackbars where you need the actual name before displaying
 * Awaits the lookup to ensure you get the real name, not a fallback
 */
export async function getUserDisplayNameAsync(auth0Id: string | undefined | null): Promise<string> {
  if (!auth0Id) return '';

  // Not an Auth0 ID, return as-is
  if (!isAuth0Id(auth0Id)) {
    return auth0Id;
  }

  // Check cache first
  const cached = globalUserCache[auth0Id];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.displayName;
  }

  // Await the lookup to get the actual name
  const result = await lookupUser(auth0Id);
  return result.displayName;
}

/**
 * Preload user names into cache (call this when you know you'll need them)
 */
export async function preloadUserNames(auth0Ids: (string | undefined | null)[]): Promise<void> {
  const validIds = auth0Ids.filter((id): id is string => !!id && isAuth0Id(id));
  if (validIds.length > 0) {
    await batchLookupUsers(validIds);
  }
}

export default useUserLookup;
